import { isRecord } from "@arti/utilities";

import type {
  DashboardRealtimeMessage,
  DashboardRealtimeSnapshot,
  DashboardRealtimeSubscriptionOptions,
} from "./dashboardRealtimeTypes";

import { RealtimeWebSocketController } from "./realtimeWebSocketController";

function isDashboardWebSocketTopic(
  value: unknown,
): value is DashboardRealtimeSubscriptionOptions["topic"] {
  return value === "internalEventTopic";
}

function isDashboardRealtimeMessage(
  value: unknown,
): value is DashboardRealtimeMessage {
  if (!isRecord(value)) {
    return false;
  }
  if (typeof value.type !== "string") {
    return false;
  }
  if (typeof value.source !== "string") {
    return false;
  }
  if (typeof value.timestamp !== "number") {
    return false;
  }
  if (typeof value.version !== "number") {
    return false;
  }
  if (!("payload" in value)) {
    return false;
  }
  return true;
}

type DashboardServerFrame =
  | { connection_id: string; heartbeat_ms: number | null; kind: "hello" }
  | {
      kind: "ack";
      request_kind: "subscribe" | "unsubscribe";
      topic: DashboardRealtimeSubscriptionOptions["topic"];
    }
  | {
      event: DashboardRealtimeMessage;
      kind: "event";
      topic: DashboardRealtimeSubscriptionOptions["topic"];
    }
  | {
      code: string;
      kind: "error";
      message: string;
      request_kind?: "subscribe" | "unsubscribe";
      topic?: DashboardRealtimeSubscriptionOptions["topic"];
    };

export function parseDashboardServerFrame(
  value: unknown,
): DashboardServerFrame | null {
  if (!isRecord(value) || typeof value.kind !== "string") {
    return null;
  }

  switch (value.kind) {
    case "hello": {
      const heartbeatMs = value.heartbeat_ms;
      if (
        typeof value.connection_id !== "string" ||
        (heartbeatMs !== null && typeof heartbeatMs !== "number")
      ) {
        return null;
      }
      return {
        connection_id: value.connection_id,
        heartbeat_ms: heartbeatMs,
        kind: "hello",
      };
    }
    case "ack": {
      if (
        value.request_kind !== "subscribe" &&
        value.request_kind !== "unsubscribe"
      ) {
        return null;
      }
      if (!isDashboardWebSocketTopic(value.topic)) {
        return null;
      }
      return {
        kind: "ack",
        request_kind: value.request_kind,
        topic: value.topic,
      };
    }
    case "error": {
      if (typeof value.code !== "string" || typeof value.message !== "string") {
        return null;
      }
      const topic = isDashboardWebSocketTopic(value.topic)
        ? value.topic
        : undefined;
      const requestKind =
        value.request_kind === "subscribe" ||
        value.request_kind === "unsubscribe"
          ? value.request_kind
          : undefined;
      return {
        code: value.code,
        kind: "error",
        message: value.message,
        ...(requestKind ? { request_kind: requestKind } : {}),
        ...(topic ? { topic } : {}),
      };
    }
    case "event": {
      if (!isDashboardWebSocketTopic(value.topic)) {
        return null;
      }
      if (!isDashboardRealtimeMessage(value.event)) {
        return null;
      }
      return { event: value.event, kind: "event", topic: value.topic };
    }
    default: {
      return null;
    }
  }
}

function resolveDisconnectStatus(
  shouldReconnect: boolean,
  kind: "close" | "error",
): DashboardRealtimeSnapshot["status"] {
  if (shouldReconnect) {
    return "reconnecting";
  }
  if (kind === "error") {
    return "error";
  }
  return "closed";
}

type SnapshotListener = (snapshot: DashboardRealtimeSnapshot) => void;

interface SubscriptionEntry {
  id: string;
  handler: (message: DashboardRealtimeMessage) => void;
  topic: DashboardRealtimeSubscriptionOptions["topic"];
}

export class DashboardRealtimeClient {
  private url: string;
  private refCount = 0;
  private destroyed = false;
  private isServerReady = false;
  private readonly wsController: RealtimeWebSocketController;

  private snapshot: DashboardRealtimeSnapshot;
  private snapshotListeners = new Set<SnapshotListener>();
  private subscriptions = new Map<string, SubscriptionEntry>();
  private topicSubscriberCounts = new Map<
    DashboardRealtimeSubscriptionOptions["topic"],
    number
  >();

  constructor(
    { url }: { url: string },
    private readonly callbacks?: { onDestroy?: () => void },
  ) {
    this.url = url;
    this.snapshot = {
      connectedAt: null,
      connectionId: null,
      heartbeatMs: null,
      isHealthy: false,
      lastError: null,
      lastMessageAt: null,
      status: "closed",
    };

    this.wsController = new RealtimeWebSocketController({
      getUrl: () => this.url,
      onConnecting: (attempt) => {
        this.updateSnapshot({
          lastError: null,
          status: attempt === 1 ? "connecting" : "reconnecting",
        });
      },
      onDisconnect: ({ kind, shouldReconnect }) => {
        this.isServerReady = false;
        const status = resolveDisconnectStatus(shouldReconnect, kind);
        this.updateSnapshot({
          isHealthy: false,
          status,
          ...(kind === "error" ? { lastError: "WebSocket error" } : {}),
        });
        if (!shouldReconnect) {
          this.maybeDestroy();
        }
      },
      onMessage: (parsed) => {
        const frame = parseDashboardServerFrame(parsed);
        if (!frame) {
          return;
        }

        this.updateSnapshot({
          lastMessageAt: this.wsController.markMessageReceived(),
        });

        if (frame.kind === "hello") {
          this.updateSnapshot({
            connectionId: frame.connection_id,
            heartbeatMs: frame.heartbeat_ms,
          });
          this.isServerReady = true;
          this.sendSubscribeForActiveTopics();
          return;
        }

        if (frame.kind === "error") {
          this.updateSnapshot({ lastError: frame.message });
          return;
        }

        if (frame.kind !== "event") {
          return;
        }

        for (const entry of this.subscriptions.values()) {
          try {
            entry.handler(frame.event);
          } catch {
            // Swallow subscriber errors (best-effort fan-out)
          }
        }
      },
      onOpen: () => {
        this.isServerReady = false;
        this.updateSnapshot({
          connectedAt: Date.now(),
          connectionId: null,
          heartbeatMs: null,
          isHealthy: true,
          lastError: null,
          status: "open",
        });
      },
      shouldReconnect: () => this.refCount > 0 && !this.destroyed,
    });
  }

  setConfig({ url }: { url: string }) {
    if (this.destroyed) {
      return;
    }

    const nextUrl = url;
    const requiresReconnect = nextUrl !== this.url;
    this.url = nextUrl;
    if (requiresReconnect) {
      this.wsController.updateUrlAndReconnectSoon({
        overrideDelayMs: 0,
        shouldReconnect: this.refCount > 0 && !this.destroyed,
      });
    }
  }

  getSnapshot() {
    return this.snapshot;
  }

  subscribeSnapshot(listener: SnapshotListener) {
    if (this.destroyed) {
      listener(this.snapshot);
      return () => {
        // No-op
      };
    }

    this.snapshotListeners.add(listener);
    listener(this.snapshot);
    return () => {
      this.snapshotListeners.delete(listener);
      this.maybeDestroy();
    };
  }

  connectRef() {
    if (this.destroyed) {
      return () => {
        // No-op
      };
    }

    this.refCount += 1;
    this.ensureConnected();
    return () => this.disconnectRef();
  }

  subscribeMessage(
    handler: (message: DashboardRealtimeMessage) => void,
    options: DashboardRealtimeSubscriptionOptions,
  ) {
    if (this.destroyed) {
      return () => {
        // No-op
      };
    }

    const id =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);

    const { topic } = options;

    const previousTopicCount = this.topicSubscriberCounts.get(topic) ?? 0;
    this.topicSubscriberCounts.set(topic, previousTopicCount + 1);
    this.subscriptions.set(id, { handler, id, topic });
    if (previousTopicCount === 0) {
      this.sendClientMessage({ kind: "subscribe", topic });
    }

    return () => {
      const existing = this.subscriptions.get(id);
      if (!existing) {
        return;
      }
      this.subscriptions.delete(id);

      const count = this.topicSubscriberCounts.get(existing.topic) ?? 0;
      if (count <= 1) {
        this.topicSubscriberCounts.delete(existing.topic);
        this.sendClientMessage({ kind: "unsubscribe", topic: existing.topic });
      } else {
        this.topicSubscriberCounts.set(existing.topic, count - 1);
      }
      this.maybeDestroy();
    };
  }

  private disconnectRef() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount === 0) {
      this.close("idle");
      this.maybeDestroy();
    }
  }

  private maybeDestroy() {
    if (this.destroyed) {
      return;
    }
    if (this.refCount !== 0) {
      return;
    }
    if (this.snapshotListeners.size !== 0) {
      return;
    }
    if (this.subscriptions.size !== 0) {
      return;
    }
    this.destroy();
  }

  private destroy() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.close("destroyed");
    this.snapshotListeners.clear();
    this.subscriptions.clear();
    this.callbacks?.onDestroy?.();
  }

  private updateSnapshot(partial: Partial<DashboardRealtimeSnapshot>) {
    const next: DashboardRealtimeSnapshot = { ...this.snapshot, ...partial };
    const changed = !snapshotEquals(next, this.snapshot);
    this.snapshot = next;
    if (!changed) {
      return;
    }
    for (const listener of this.snapshotListeners) {
      listener(this.snapshot);
    }
  }

  private ensureConnected() {
    if (this.destroyed) {
      return;
    }
    this.wsController.ensureConnected();
  }

  private close(reason: string) {
    this.wsController.close(reason);
  }

  private sendSubscribeForActiveTopics() {
    for (const topic of this.topicSubscriberCounts.keys()) {
      this.sendClientMessage({ kind: "subscribe", topic });
    }
  }

  private sendClientMessage(frame: {
    kind: "subscribe" | "unsubscribe";
    topic: DashboardRealtimeSubscriptionOptions["topic"];
  }) {
    if (!this.wsController.isOpen() || !this.isServerReady) {
      return;
    }
    try {
      this.wsController.sendText(JSON.stringify(frame));
    } catch {
      // Ignore
    }
  }
}

function snapshotEquals(
  a: DashboardRealtimeSnapshot,
  b: DashboardRealtimeSnapshot,
): boolean {
  return (
    a.status === b.status &&
    a.isHealthy === b.isHealthy &&
    a.connectionId === b.connectionId &&
    a.heartbeatMs === b.heartbeatMs &&
    a.connectedAt === b.connectedAt &&
    a.lastMessageAt === b.lastMessageAt &&
    a.lastError === b.lastError
  );
}

export type {
  DashboardRealtimeMessage,
  DashboardRealtimeSnapshot,
  DashboardRealtimeSubscriptionOptions,
} from "./dashboardRealtimeTypes";
